import styled, { keyframes } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';

import { BusyInner, BusyOverlay, BusyText } from '../VideoLibrary/VideoLibrary.styles';
import { ANIM, BAR_COLORS, COLORS, GRADIENTS, MOBILE, REDUCED_MOTION, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

/* The banner-kit composer: the video easy editor's header (name + version), split and guide, so the two editors read
   as one system. The left rail holds the Art Style page's ingredient sections (subject, sizes, art style, character,
   direction) over a pinned Generate; the right pane holds the size grid over pinned downloads. Stacked on
   mobile. */

export {
  EasyHeader as Header,
  EasyHeaderLeft as HeaderLeft,
  EasyTitleEditIcon as TitleEditIcon,
  EasyTitleInput as TitleInput,
  EasyTitleWrap as TitleWrap,
  EasySplit as Split,
  EasyRail as KitRail,
  EasyOutput as KitOutput,
  EasyOutputScroll as OutputScroll,
  EasyGuide as Guide,
  EasyGuideIcon as GuideIcon,
  EasyGuideTitle as GuideTitle,
  EasyGuideText as GuideText,
  EasyGuideSteps as GuideSteps,
  EasyGuideStep as GuideStep,
  EasyGuideStepNum as GuideStepNum,
  EasyGuideStepText as GuideStepText,
} from '../StoryboardComposer/StoryboardComposer.styles';

/** The height of the cell every size is fitted into. Layout, not a timing. */
/** A size card's narrowest width: the actions menu (200px) plus the footer's end padding and some slack. Layout. */
/** A masonry column is never narrower than a tile's actions menu (200px wide, opening leftward from the caption's end),
 *  so the menu always opens inside its own card and the scrolling pane never clips it. */
export const KIT_TILE_MIN_W_PX = 212;
/** The gap between masonry columns and between the cards in a column. */
export const KIT_GRID_GAP_PX = 12;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
  gap: ${SPACE[24]};
  align-items: start;

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** The setup column: one block per section. */
export const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[24]};
  min-width: 0;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]};
`;

export const SectionTitle = styled(Text).attrs({ tag: 'h3' as const })`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const Hint = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};
`;

/** "Draft with Lumio": a quiet pill with the Lumio glyph (an AI affordance, never a sparkle). */
export const LumioBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[12]};
  border-radius: 999px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* ── The rail: ingredient sections scroll, Generate is pinned ── */

/** The rail's scrolling stack of ingredient sections (each draws its own divider). */
export const RailScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Reserve the scrollbar's room up front (as the video ad rail does): opening a long section showed a scrollbar that
     narrowed the column and shifted every row sideways. */
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  /* Room for the open section's background, which reaches past the column on both sides. */
  padding: 0 ${SPACE[12]};
`;

/** The rail's pinned foot: the status line over Generate (or Stop while a run is in flight). */
export const RailFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding-top: ${SPACE[16]};
  border-top: 1px solid ${COLORS.hairline};
`;

export const RailStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

/** The right pane's pinned foot: the generated version's downloads. */
export const OutputFoot = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
  padding-top: ${SPACE[16]};
  border-top: 1px solid ${COLORS.hairline};
`;

/* ── The subject (read-only: its own copy is what every size paints) ── */

export const SubjectCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[12]};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const SubjectThumb = styled.img`
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
`;

export const SubjectCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

export const CopyHeadline = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: ${COLORS.textBright};
`;

export const CopySub = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

/** The Art style section: the brand's active style, then what the selected version was drawn with. */
export const ArtStyleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding-bottom: ${SPACE[8]};
`;

export const LockNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[8]};
  margin: ${SPACE[12]} 0 ${SPACE[16]};
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};

  svg {
    flex: 0 0 auto;
    /* Optical: centres the 12px glyph on the first line of 12px / 1.45 text. */
    margin-top: 2px;
  }
`;

/* ── Kits ── */

export const KitList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  overflow: hidden;
`;

export const KitRow = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }
`;

export const KitHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]} ${SPACE[12]};
  background: ${COLORS.surfaceFaint};
`;

export const KitCheck = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const KitCount = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const ExpandBtn = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;

  svg {
    transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
    transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }
`;

export const SizeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[8]};
  display: flex;
  flex-direction: column;
`;

export const SizeRow = styled.li<{ $off: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[4]} 0;
  opacity: ${(p) => (p.$off ? 0.5 : 1)};

  /* A social size's networks wrap over lines; the switch keeps its full width instead of shrinking to its knob. */
  & > [role='switch'] {
    flex: 0 0 auto;
  }
`;

/** The size and, for a social size, the networks it fits under it. */
export const SizeInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

export const SizeName = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.text};
`;

export const SizeNetworks = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.textFaint};
`;

export const SizeMeta = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/* ── Legal line ── */

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
`;

export const ToggleLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  color: ${COLORS.text};
`;

/* ── Output: the size grid ── */

export const Output = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-width: 0;
`;

/** The sizes as a masonry: balanced columns (SizeGrid deals the tiles into them, each into the shortest), every size
 *  at its own aspect ratio across the column's full width, so no card carries empty space around its banner. */
export const Masonry = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${KIT_GRID_GAP_PX}px;
`;

export const MasonryColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${KIT_GRID_GAP_PX}px;
`;

/** The ultra-wide strips (leaderboards, mobile banners): a band of their own under the masonry, where a column would
 *  shrink them to a sliver. */
export const Strips = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  align-items: start;
  gap: ${KIT_GRID_GAP_PX}px;
  margin-top: ${KIT_GRID_GAP_PX}px;
`;

/** A size's card: its banner edge to edge on top, the size and its actions in a slim caption. Not clipped (the actions
 *  menu opens out of it); the banner frame and the generating overlay round their own corners. */
export const Tile = styled.figure<{ $wide?: boolean }>`
  position: relative;
  margin: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Only for a grid that still places cards in cells (the HTML5 banner sizes): a wide strip spans two. */
  grid-column: ${(p) => (p.$wide ? 'span 2' : 'auto')};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }
`;

/** The banner at its size's exact aspect ratio, the card's full width (the card's inner top corners: 12px - 1px). */
export const TileFrame = styled.div<{ $ratio: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => p.$ratio};
  border-radius: 11px 11px 0 0;
  overflow: hidden;
  background: ${COLORS.surfaceFaint};
`;

/** The shape render, cropped to the size's exact aspect (what the export does with a cover-resize). */
export const TileImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: inherit;
`;

export const TileCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[4]};
  text-align: center;
`;

/** A size Lumio is drawing (or separating into layers): the library cards' generating treatment (the drifting Lumio
 *  dot lattice behind a scrim, the Lumio mark and a short line) across the WHOLE card, banner and caption alike; the
 *  caption's size label stays readable on top of it. */
export const TileGen = styled(BusyOverlay)`
  border-radius: 11px;
  overflow: hidden;
`;

export const TileGenInner = styled(BusyInner)`
  gap: ${SPACE[4]};
  padding: ${SPACE[4]};
  text-align: center;
`;

/** The tile's line; unlike the library card's, it stays on phones (a size tile has room for it). */
export const TileGenText = styled(BusyText)`
  ${MOBILE} {
    display: inline;
  }
`;

export const TileNote = styled(Text).attrs({ tag: 'span' as const })<{ $tone?: 'muted' | 'danger' }>`
  font-size: 11px;
  line-height: 1.3;
  color: ${(p) => (p.$tone === 'danger' ? STATUS_COLORS.negative : COLORS.textFaint)};
`;

export const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${COLORS.hairline};
  border-top-color: ${COLORS.textMuted};
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** The tile's action menu, pinned to its top-right corner over the render. */
/** The tile's actions, at the end of its caption row (never over the render, which a thin strip could not hold). */
export const TileMenu = styled.div`
  margin-left: auto;
`;

/** The card's footer: the size, any badge, and the actions menu at the end. */
export const TileCaption = styled.figcaption<{ $busy?: boolean }>`
  position: relative;
  /* Over the generating overlay (z-index 2), so the size still reads while the dots run under it. */
  z-index: 3;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  /* Holds the actions menu's trigger (32px, 48px on phones) with room above and below, so it never spills out. */
  min-height: 40px;
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[12]};
  border-top: 1px solid ${(p) => (p.$busy ? 'transparent' : COLORS.hairline)};
`;

export const TileSize = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.text};
`;

export const TileMeta = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

/** "Needs review", focusable: its reason shows in ReviewTipText (ReviewBadgeTip places it). */
export const ReviewTip = styled.span`
  display: inline-flex;
  min-width: 0;
  border-radius: 999px;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${COLORS.accent};
  }
`;

/** The reason tooltip, on the page's top layer (fixed to the screen, above the menus' layer): the action menu's
 *  popover surface (inset, panel border, menu shadow). Hidden until placed, so it never flashes at the corner. */
export const ReviewTipText = styled.span<{ $shown: boolean }>`
  position: fixed;
  /* Above open sheets (2000) and with the toasts (2100): the area has no z-index scale, so the literal follows theirs. */
  z-index: 2100;
  width: max-content;
  max-width: 240px;
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 10px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  box-shadow: ${COLORS.menuShadow};
  color: ${COLORS.text};
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  pointer-events: none;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/** The QA flagged this render (see ReviewTip for its reason). */
export const ReviewBadge = styled(Text).attrs({ tag: 'span' as const })`
  flex: none;
  white-space: nowrap;
  padding: 1px ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${BAR_COLORS.warning};
  color: ${BAR_COLORS.warning};
  font-size: 11px;
  font-weight: 600;
`;

export const EmptyGrid = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  padding: ${SPACE[24]} ${SPACE[12]};
  text-align: center;
  font-size: 13px;
  color: ${COLORS.textFaint};
  border-radius: 12px;
  border: 1px dashed ${COLORS.hairline};
`;

/* ── Footer ── */

export const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[12]};
  width: 100%;
`;

export const FootStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  flex: 1 1 220px;
  min-width: 0;
`;

export const FootText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: ${COLORS.surfaceInset};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  /* Floored so a run still on its hero shows a visible sliver rather than an empty track. */
  width: ${(p) => Math.max(3, Math.min(100, p.$pct))}%;
  border-radius: inherit;
  background: ${GRADIENTS.deploymentProgress};
  transition: width ${ANIM.BAR_DURATION_MS}ms ${ANIM.EASE};
`;

export const FootActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${SPACE[8]};
`;

/* ── Redraw sheet ── */

/* ── Export results (per-file rows: the kit's HTML5 export and the mini-game export share them) ── */

export const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const FileRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 10px;
  background: ${COLORS.surfaceFaint};
`;

export const FileHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const FileName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.text};
`;

export const FileMeta = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const BudgetOk = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${STATUS_COLORS.positive};
`;

export const BudgetOver = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${BAR_COLORS.warning};
`;

export const Violation = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

/** A result row's own downloads (the size's package, its backup image), pushed to the row's end. */
export const FileActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[4]};
  margin-left: auto;
`;

/* ── Animated HTML5 export sheet ── */

export const ExportSheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/** The sizes to package, as a grid of checkboxes. */
export const SizeChecks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: ${SPACE[8]} ${SPACE[16]};
`;

/* The layer separation sheet's body and its preview of the size (shared with the old redraw sheet's layout). */
export const RedrawBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const RedrawPreview = styled.div<{ $ratio: number }>`
  position: relative;
  width: min(100%, ${(p) => Math.round(200 * p.$ratio)}px);
  aspect-ratio: ${(p) => p.$ratio};
  margin-inline: auto;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;
